<template>
  <div>
    <DxForm id="formularioRappelEnergia" ref="formInstance" :form-data="model" label-location="top"
      :show-colon-after-label="false" :visible="model != null" validation-group="validacionRappelEnergia">
      <!-- xs: 768, sm: 992, md:1200 -->
      <DxColCountByScreen :xs="4" :sm="4" :md="4" :lg="4" />

      <DxGroupItem :col-span="4" :col-count="4">
        <DxSimpleItem data-field="EmpresaServicios" editor-type="dxTextBox" :col-span="4" :editor-options="{ readOnly: true }">
          <DxLabel :text="$t('rappel_energia.form.nombre_empresa_servicios')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="FechaInicio" :editor-options="{ disabledDates: disabledDaysInicio }" editor-type="dxDateBox"
          :col-span="1">
          <DxLabel :text="$t('rappel_energia.form.fecha_inicio')" />
          <DxRequiredRule :message="$t('rappel_energia.form.validacion.fecha_inicio')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="FechaFin" editor-type="dxDateBox" :col-span="1" :is-required="true">
          <DxDateBox v-model="props.model!.FechaFin" :disabled-dates="disabledDaysFin" validation-message-mode="always">
            <DxValidator ref="validatorFechaFin" validation-group="validacionFechaFin">
              <DxRequiredRule :message="$t('rappel_energia.form.validacion.fecha_fin')" />
              <DxCompareRule :comparison-target="fechaInicio" :comparison-type="'>'"
                :message="$t('rappel_energia.form.validacion.fecha_fin_mayor')" :reevaluate="true" />
            </DxValidator>
          </DxDateBox>
          <DxLabel :text="$t('rappel_energia.form.fecha_fin')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="TipoElemento" editor-type="dxSelectBox" :col-span="1" :editor-options="selectTipoElemento">
          <DxLabel :text="$t('rappel_energia.form.tipo_elemento')" />
          <DxRequiredRule :message="$t('rappel_energia.form.validacion.tipo_elemento')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="TipoSuministro" editor-type="dxSelectBox" :col-span="1"
          :editor-options="selectTipoSuministro">
          <DxLabel :text="$t('rappel_energia.form.tipo_energia')" />
          <DxRequiredRule :message="$t('rappel_energia.form.validacion.tipo_suministro')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="VolumenMin" editor-type="dxNumberBox" :col-span="1" :editor-options="volumenMinNumberOptions">
          <DxLabel :text="$t('rappel_energia.form.volumen_min')" />
          <DxRequiredRule :message="$t('rappel_energia.form.validacion.volumen_minimo')" />
          <DxCompareRule :comparison-target="() => 0" :comparison-type="'>='"
            :message="$t('rappel_energia.form.validacion.volumen_min_mayor_igual_cero')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Importe" editor-type="dxNumberBox" :col-span="1" :editor-options="importeNumberOptions">
          <DxLabel :text="$t('rappel_energia.form.importe')" />
          <DxRequiredRule :message="$t('rappel_energia.form.validacion.importe')" />
          <DxCompareRule :comparison-target="() => 0" :comparison-type="'>='"
            :message="$t('rappel_energia.form.validacion.importe_mayor_cero')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="PotenciaMax" editor-type="dxNumberBox" :col-span="1" :editor-options="potenciaNumberOptions"
          :visible="model?.TipoSuministro == TipoSuministroTramiteEnergiaEnum.Luz">
          <DxLabel :text="$t('rappel_energia.form.potencia_max')" />
          <DxRequiredRule :message="$t('rappel_energia.form.validacion.potencia_max')" />
          <DxCompareRule :comparison-target="() => 0" :comparison-type="'>'"
            :message="$t('rappel_energia.form.validacion.potencia_max_mayor_cero')" />
        </DxSimpleItem>
        <DxEmptyItem :col-span="2" :visible="model?.TipoSuministro == TipoSuministroTramiteEnergiaEnum.Gas"></DxEmptyItem>
        <DxEmptyItem :col-span="1" :visible="model?.TipoSuministro == TipoSuministroTramiteEnergiaEnum.Luz"></DxEmptyItem>
        <DxSimpleItem data-field="Nombre" editor-type="dxTextBox" :col-span="2">
          <DxLabel :text="$t('rappel_energia.form.nombre')" />
          <DxStringLengthRule :max="255" :message="$t('rappel_energia.form.validacion.nombre_longitud')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="IdEmpresaAgentes" editor-type="dxSelectBox" :col-span="2"
          :editor-options="{ ...getSelectBoxDefaultProperties(), ...selectEmpresaAgentesOptions }">
          <DxLabel :text="$t('rappel_energia.form.empresa_agentes')" />
        </DxSimpleItem>
      
      </DxGroupItem>
    </DxForm>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from '@components/dialogs/message-box';
import { TipoSuministroTramiteEnergiaEnum, type CreateRappelEnergiaModel, type DetailRappelEnergiaModel, type EditRappelEnergiaModel } from '@api/api';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { type DxButton } from 'devextreme-vue';
import {
  DxForm,
  DxItem,
  DxLabel,
  DxColCountByScreen,
  DxRequiredRule,
  DxCompareRule,
  DxEmptyItem,
  DxSimpleItem,
  DxGroupItem,
  DxStringLengthRule
} from "devextreme-vue/form";
import DxValidator from 'devextreme-vue/validator';
import { DxDateBox } from "devextreme-vue";
import { getCurrentInstance, onBeforeMount, onMounted, reactive, ref, watch } from 'vue';
import { useIntl } from '@services/i18n';
import { getLookup } from '@helpers/grid-helper';
import validationEngine from "devextreme/ui/validation_engine";
import { useApi } from '@api/integration';
import { toUTCDateOnly } from '@/.shared/helpers/date-parser-helper';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { getSelectBoxDefaultProperties } from "@helpers/grid-helper";

const api = useApi();
const formInstance = ref<DxForm>();
const intl = useIntl();
const props = defineProps({
  model: {
    type: Object as () => DetailRappelEnergiaModel | CreateRappelEnergiaModel,
    required: true
  },
  isEditMode: {
    type: Boolean,
    required: true
  }
});

const handleTipoSuministroChange = (e: any) => {
  switch (e.value) {
    case TipoSuministroTramiteEnergiaEnum.Luz:
      props.model.PotenciaMax = 0;
      break;

    case TipoSuministroTramiteEnergiaEnum.Gas:
      props.model.PotenciaMax = null;
      break;
  }
}

const selectEmpresaAgentesOptions = reactive<any>({
  showClearButton: true
});

const selectTipoElemento = { ...getLookup('rappels', 'tipo_elemento') };
const selectTipoSuministro = { ...getLookup('rappels', 'tipo_suministro'), onValueChanged: handleTipoSuministroChange };

watch(
  () => ({
    fechaInicio: props.model.FechaInicio,
    fechaFin: props.model.FechaFin
  }),
  () => {
    validationEngine.validateGroup("validacionFechaFin");
  }
)

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

onBeforeMount(async () => {
  selectEmpresaAgentesOptions.dataSource = await api.callStandardApi('getEmpresaAgentesSelector', { showLoading: false });
});

function disabledDaysInicio(data: any) {
  // Habilitar solo el primer día del mes
  return data.date.getDate() !== 1;
}

function disabledDaysFin(data: any) {
  // Habilitar solo el último día del mes
  const lastDay = new Date(data.date.getFullYear(), data.date.getMonth() + 1, 0).getDate();
  return data.date.getDate() !== lastDay;
}

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const saveOrEdit = async () => {
  var res1 = validationEngine.validateGroup("validacionRappelEnergia");
  var res2 = validationEngine.validateGroup("validacionFechaFin");

  if (res1.isValid && res2.isValid) {
    props.model.FechaInicio = toUTCDateOnly(props.model.FechaInicio);
    props.model.FechaFin = toUTCDateOnly(props.model.FechaFin);

    if (props.isEditMode) {
      await api.callStandardApi("editRappelEnergia", { showLoading: true }, props.model as EditRappelEnergiaModel)
        .then(() => toastSuccess(intl.i18n.t('rappel_energia.edicion_exito')))
        .catch(() => {
          toastError(intl.i18n.t('rappel_energia.edicion_fallo'))
          return false;
        });
    } else {
      await api.callStandardApi("createRappelEnergia", { showLoading: true }, props.model as CreateRappelEnergiaModel)
        .then(() => toastSuccess(intl.i18n.t('rappel_energia.creacion_exito')))
        .catch(() => {
          toastError(intl.i18n.t('rappel_energia.creacion_fallo'));
          return false;
        });
    }
    return true;
  } else {
    toastError(intl.i18n.t('rappel_energia.revise_validacion'));
  }

  return false;
}

const volumenMinNumberOptions = {
  showSpinButtons: true,
  step: 1,
  format: {
    type: "fixedPoint",
    precision: 0
  },
  min: 0
};

const importeNumberOptions = {
  showSpinButtons: true,
  step: 0.01,
  format: {
    type: "fixedPoint",
    precision: 2,
    formatter: (value: number) => {
      return intl.i18n.n(Number(value.toFixed(2)), 'decimal') + ' €';
    }
  },
  min: 0
};

const potenciaNumberOptions = {
  showSpinButtons: true,
  step: 0.01,
  format: {
    type: "fixedPoint",
    precision: 3,
    formatter: (value: number) => {
      return intl.i18n.n(Number(value.toFixed(2)), 'decimal') + ' kW';
    }
  }
};

function fechaInicio() {
  return props.model.FechaInicio;
}

</script>
<style lang="scss" scoped></style>
